import React, { useState } from "react";
import axios from "axios";
import { API_URL } from "../config/api";
import GoogleLoginButton from "../components/GoogleLoginButton";
import Logo from "../assets/teakonn-logo.png";

// Use centralized, normalized API base

export default function Login({ onSuccess, switchToRegister }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const emailRef = React.useRef<HTMLInputElement | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotMessage, setForgotMessage] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState<string | null>(() => {
    try { return localStorage.getItem('auralink-reset-token'); } catch { return null; }
  });
  const [newPassword, setNewPassword] = useState('');
  const [confirmNew, setConfirmNew] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const res = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { token, user } = res.data;

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));

      onSuccess({ token, user });
    } catch (err) {
      const msg = String(err?.response?.data?.message || '').toLowerCase();
      if (msg.includes('invalid') || msg.includes('credentials') || msg.includes('not found')) {
        setError("Invalid email or password. Please try again.");
      } else {
        setError("Login failed — please check your details and try again.");
      }
      try { emailRef.current?.focus(); } catch {}
    }
    setSubmitting(false);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#071229] px-4">
      <div className="bg-slate-800/60 p-10 rounded-xl w-full max-w-md text-slate-100">

        {/* LOGO */}
        <div className="flex justify-center mb-6">
          <img src={Logo} alt="TeaKonn Logo" className="h-16 w-auto object-contain drop-shadow-lg" />
        </div>

        {/* TITLE */}
        <h2 className="text-3xl font-extrabold text-center mb-6 bg-gradient-to-r from-cyan-400 to-violet-400 bg-clip-text text-transparent">
          Log In
        </h2>

        {error && (
          <div className="text-red-400 mb-3 text-center" role="alert" aria-live="polite">{error}</div>
        )}

        {resetToken ? (
          <form className="flex flex-col gap-4" onSubmit={async (e) => {
            e.preventDefault();
            setResetSubmitting(true);
            setResetMessage(null);
            if (newPassword.length < 8) {
              setResetMessage('Password must be at least 8 characters.');
              setResetSubmitting(false);
              return;
            }
            if (newPassword !== confirmNew) {
              setResetMessage('Passwords do not match.');
              setResetSubmitting(false);
              return;
            }
            try {
              await axios.post(`${API_URL}/auth/reset-password`, { token: resetToken, newPassword: newPassword });
              setResetMessage('Password reset successful. You can now log in.');
              try { localStorage.removeItem('auralink-reset-token'); } catch {}
              setResetToken(null);
            } catch (err) {
              setResetMessage('Reset link invalid or expired. Please request a new one.');
            }
            setResetSubmitting(false);
          }}>
            <input
              placeholder="New password"
              type="password"
              className="p-3 rounded-md bg-slate-900/50 border border-slate-700 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/40 outline-none"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={resetSubmitting}
            />
            <input
              placeholder="Confirm new password"
              type="password"
              className="p-3 rounded-md bg-slate-900/50 border border-slate-700 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/40 outline-none"
              value={confirmNew}
              onChange={(e) => setConfirmNew(e.target.value)}
              disabled={resetSubmitting}
            />
            <button className="btn w-full mt-2 disabled:opacity-60" type="submit" disabled={resetSubmitting}>
              {resetSubmitting ? 'Resetting…' : 'Set New Password'}
            </button>
            {resetMessage && (
              <div className="text-sm text-theme-secondary mt-2 text-center">{resetMessage}</div>
            )}
            <button type="button" className="text-sm text-cyan-300 hover:underline mt-2 self-start" onClick={() => { setResetToken(null); try { localStorage.removeItem('auralink-reset-token'); } catch {} }}>Back to login</button>
          </form>
        ) : !forgotOpen ? (
        <form className="flex flex-col gap-4" onSubmit={handleLogin}>
          <input
            placeholder="Email"
            type="email"
            className="p-3 rounded-md bg-slate-900/50 border border-slate-700 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/40 outline-none"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
            disabled={submitting}
            ref={emailRef}
          />

          <input
            placeholder="Password"
            type="password"
            className="p-3 rounded-md bg-slate-900/50 border border-slate-700 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/40 outline-none"
            value={password}
            onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
            disabled={submitting}
          />

          <button className="btn w-full mt-2 disabled:opacity-60" type="submit" disabled={submitting}>
            {submitting ? "Logging in…" : "Log In"}
          </button>
          <button type="button" className="text-sm text-cyan-300 hover:underline mt-2 self-start" onClick={() => { setForgotOpen(true); setForgotMessage(null); }}>Forgot password?</button>
        </form>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={async (e) => {
            e.preventDefault();
            setForgotSubmitting(true);
            setForgotMessage(null);
            try {
              await axios.post(`${API_URL}/auth/forgot-password`, { email });
              setForgotMessage('If an account exists for that email, we have sent a reset link.');
            } catch (err) {
              setForgotMessage('Could not request reset at this time. Please try again later.');
            }
            setForgotSubmitting(false);
          }}>
            <input
              placeholder="Enter your email"
              type="email"
              className="p-3 rounded-md bg-slate-900/50 border border-slate-700 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/40 outline-none"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
              disabled={forgotSubmitting}
            />
            <button className="btn w-full mt-2 disabled:opacity-60" type="submit" disabled={forgotSubmitting}>
              {forgotSubmitting ? 'Requesting…' : 'Send Reset Link'}
            </button>
            {forgotMessage && (
              <div className="text-sm text-theme-secondary mt-2 text-center">{forgotMessage}</div>
            )}
            <button type="button" className="text-sm text-cyan-300 hover:underline mt-2 self-start" onClick={() => setForgotOpen(false)}>Back to login</button>
          </form>
        )}

        {/* Or divider */}
        <div className="my-6 flex items-center gap-3 opacity-70">
          <div className="h-px bg-slate-600 flex-1" />
          <span className="text-sm">OR</span>
          <div className="h-px bg-slate-600 flex-1" />
        </div>

        {/* Google Login */}
        <GoogleLoginButton onSuccess={onSuccess} className="flex justify-center" />

        <p className="mt-6 text-sm text-center opacity-80">
          Don’t have an account?{" "}
          <button onClick={switchToRegister} className="text-cyan-300 hover:underline">
            Register
          </button>
        </p>
      </div>
    </div>
  );
}
